import * as Yup from 'yup';

const toOptionalNumber = (schema: Yup.NumberSchema<number | undefined>) =>
  schema.transform((val, orig) =>
    orig === '' || orig === null ? undefined : val
  );

const requiredText = () => Yup.string().trim().required('Required');

export const coversValidationSchema = (
  lineExternalCode?: string,
  rowIsEnabled?: (row: any) => boolean,
  amendmentType?: string
) => {
  const isLine51 = lineExternalCode.toString() === '51';
  const isLine3 = lineExternalCode?.toString() === '3';
  const isCancellation = amendmentType === 'PolicyCancellation';
  const rowSchema = Yup.object({
    policyCover: Yup.string().required('Required'),
    isMain: Yup.boolean().required('Required'),
    isMandatory: Yup.boolean().required('Required'),
    coverEffectiveFrom: Yup.string().required('Required'),
    coverEffectiveTo: Yup.string().when([], {
      is: () => !isLine3,
      then: (s) => s.required('Required'),
      otherwise: (s) => s.notRequired()
    }),

    sumInsuredIsAdditive: Yup.boolean().required('Required'),
    coverSumInsured: requiredText(),

    coverPremiumType: Yup.string().when([], {
      is: () => isLine51,
      then: (s) => s.required('Required'),
      otherwise: (s) => s.notRequired()
    }),

    coverPremiumAmount: Yup.mixed().when(
      ['coverPremiumType'],
      ([typ], _schema: Yup.Schema<any>) => {
        const t = typeof typ === 'string' ? typ : '';
        if (t === 'AMOUNT') return requiredText();

        return toOptionalNumber(Yup.number());
      }
    ),

    excessOnClaimType: Yup.string().required('Required'),

    excessOnClaimAmount: Yup.string().when('excessOnClaimType', {
      is: 'AMOUNT',
      then: (schema) => schema.required('Required'),
      otherwise: (schema) => schema.notRequired()
    }),

    excessOnClaimDays: Yup.string().when('excessOnClaimType', {
      is: 'DAYS',
      then: (schema) => schema.required('Required'),
      otherwise: (schema) => schema.notRequired()
    }),

    excessOnClaimPercentage: Yup.string().when('excessOnClaimType', {
      is: 'PERCENTAGE',
      then: (schema) => schema.required('Required'),
      otherwise: (schema) => schema.notRequired()
    }),

    // Premium Variance field for cancellation scenarios with range validation
    coverPremiumVariance: Yup.number()
      .transform((val, orig) => {
        // Handle partial typing of negative numbers
        if (orig === '' || orig === null || orig === undefined)
          return undefined;
        if (orig === '-' || orig === '+') return undefined; // Allow typing - or + without validation error
        return val;
      })
      .when(['coverPremiumAmount'], {
        is: () => isCancellation,
        then: (schema) => {
          return schema.required('Required');
        },
        otherwise: (schema) => schema.notRequired()
      }),

    // For non-cancellation scenarios, these fields are required
    newSumInsured: Yup.string().when([], {
      is: () => !isCancellation,
      then: (schema) => schema.required('Required'),
      otherwise: (schema) => schema.notRequired()
    }),

    newPremiumAmount: Yup.mixed().when(['coverPremiumType'], {
      is: 'AMOUNT',
      then: (schema) => {
        if (isCancellation) return schema.notRequired();
        return schema.required('Required');
      },
      otherwise: (s) => s.notRequired()
    }),

    newPremiumPercentage: Yup.mixed().when(['coverPremiumType'], {
      is: 'PERCENTAGE',
      then: (schema) => {
        if (isCancellation) return schema.notRequired();
        return schema.required('Required');
      },
      otherwise: (s) => s.notRequired()
    }),

    newExcessAmount: Yup.mixed().when(['excessOnClaimType'], {
      is: 'AMOUNT',
      then: (schema) => {
        if (isCancellation) return schema.notRequired();
        return schema.required('Required');
      },
      otherwise: (s) => s.notRequired()
    }),

    newExcessPercentage: Yup.mixed().when(['excessOnClaimType'], {
      is: 'PERCENTAGE',
      then: (schema) => {
        if (isCancellation) return schema.notRequired();
        return schema.required('Required');
      },
      otherwise: (s) => s.notRequired()
    }),

    newExcessDays: Yup.mixed().when(['excessOnClaimType'], {
      is: 'DAYS',
      then: (schema) => {
        if (isCancellation) return schema.notRequired();
        return schema.required('Required');
      },
      otherwise: (s) => s.notRequired()
    })
  }).test('enabled-skip', 'ok', function (row) {
    if (rowIsEnabled && !rowIsEnabled(row)) return true;

    return true;
  });

  return Yup.object({
    covers: Yup.array().of(rowSchema)
  });
};
